import {
  AlertCircle,
  Check,
  Image as ImageIcon,
  Video as VideoIcon,
  Linkedin,
  Facebook,
  Instagram,
  Loader2,
  Share2,
  Music,
  Search,
  Play,
  Square,
  X,
} from "lucide-react";
import { SiGoogle } from "react-icons/si";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "../../contexts/LanguageContext";
import Button from "../ui/Button";
import Modal from "../ui/Modal";
import { cn } from "../../utils/cn";
import postsService, { AudioTrack } from "../../services/postsService";

export interface AudioConfig {
  audio_id: string;
  audio_volume: number;
  video_volume: number;
  track: AudioTrack;
}

interface MultiPlatformPublishModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPublish: (platforms: string[], audioConfig?: AudioConfig) => Promise<void>;
  post: any;
  loading?: boolean;
}

const PLATFORMS = [
  {
    id: "google_my_business",
    name: "Google Business",
    description: "Google Business Profile (Minha Empresa)",
    requiresVideo: false,
    icon: <SiGoogle className="h-5 w-5" />,
  },
  {
    id: "linkedin",
    name: "LinkedIn",
    description: "Feed principal do LinkedIn",
    requiresVideo: false,
    icon: <Linkedin className="h-5 w-5" />,
  },
  {
    id: "instagram_feed",
    name: "Instagram Feed",
    description: "Publicação normal no feed do Instagram (imagem ou vídeo)",
    requiresVideo: false,
    icon: <Instagram className="h-5 w-5" />,
  },
  {
    id: "instagram_reels",
    name: "Instagram Reels",
    description: "Vídeos curtos no Instagram",
    requiresVideo: true,
    supportsAudio: true,
    icon: <Instagram className="h-5 w-5" />,
  },
  {
    id: "facebook_reels",
    name: "Facebook Reels",
    description: "Vídeos curtos no Facebook",
    requiresVideo: true,
    supportsAudio: true,
    icon: <Facebook className="h-5 w-5" />,
  },
  {
    id: "facebook_page",
    name: "Facebook Page",
    description: "Página do Facebook",
    requiresVideo: false,
    icon: <Facebook className="h-5 w-5" />,
  },
  {
    id: "instagram_stories",
    name: "Instagram Stories",
    description:
      "Publicação automática (Business) ou assistida (Personal/Creator)",
    requiresVideo: false,
    icon: <Instagram className="h-5 w-5" />,
    disabled: false,
    warning: "Utiliza automação direta ou assistente conforme o tipo de conta",
  },
  {
    id: "facebook_stories",
    name: "Facebook Stories",
    description: "Stories na página do Facebook (vídeo ou imagem)",
    requiresVideo: false,
    icon: <Facebook className="h-5 w-5" />,
  },
  {
    id: "youtube",
    name: "YouTube",
    description: "Upload de vídeo no canal do YouTube",
    requiresVideo: true,
    icon: <VideoIcon className="h-5 w-5 text-red-600" />,
  },
];

const REELS_PLATFORM_IDS = new Set(["instagram_reels", "facebook_reels"]);

function formatDuration(sec: number | null): string {
  if (sec == null) return "";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

const MultiPlatformPublishModal: React.FC<MultiPlatformPublishModalProps> = ({
  isOpen,
  onClose,
  onPublish,
  post,
  loading = false,
}) => {
  const { t } = useTranslation();
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [publishing, setPublishing] = useState(false);

  // Audio state
  const [audioSearch, setAudioSearch] = useState("");
  const [audioTracks, setAudioTracks] = useState<AudioTrack[]>([]);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [selectedAudio, setSelectedAudio] = useState<AudioConfig | null>(null);
  const [audioVolume, setAudioVolume] = useState(80);
  const [videoVolume, setVideoVolume] = useState(50);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hasReelsPlatform = selectedPlatforms.some((p) =>
    REELS_PLATFORM_IDS.has(p),
  );

  // Load trending audio when Reels panel first opens
  useEffect(() => {
    if (hasReelsPlatform && audioTracks.length === 0 && !loadingAudio) {
      fetchAudio("");
    }
  }, [hasReelsPlatform]);

  // Debounce search
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchAudio(audioSearch);
    }, 500);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [audioSearch]);

  // Stop audio on unmount / close
  useEffect(() => {
    if (!isOpen) {
      stopPreview();
      setSelectedPlatforms([]);
      setSelectedAudio(null);
      setAudioSearch("");
      setAudioTracks([]);
    }
  }, [isOpen]);

  const fetchAudio = useCallback(
    async (q: string) => {
      setLoadingAudio(true);
      setAudioError(null);
      try {
        const res = await postsService.searchInstagramAudio(q || undefined, 20);
        if (res.success) {
          setAudioTracks(res.data);
        } else {
          setAudioError(
            t(
              "Não foi possível carregar músicas. Verifique se a conta Instagram está configurada.",
            ),
          );
          setAudioTracks([]);
        }
      } catch {
        setAudioError(t("Erro de conexão ao buscar músicas."));
        setAudioTracks([]);
      } finally {
        setLoadingAudio(false);
      }
    },
    [t],
  );

  const stopPreview = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingId(null);
  };

  const togglePreview = (track: AudioTrack) => {
    if (playingId === track.id) {
      stopPreview();
      return;
    }
    stopPreview();
    if (!track.preview_url) return;
    const audio = new Audio(track.preview_url);
    audio.onended = () => setPlayingId(null);
    audio.play().catch(() => setPlayingId(null));
    audioRef.current = audio;
    setPlayingId(track.id);
  };

  const selectTrack = (track: AudioTrack) => {
    stopPreview();
    setSelectedAudio({
      audio_id: track.id,
      audio_volume: audioVolume,
      video_volume: videoVolume,
      track,
    });
  };

  const clearTrack = () => {
    stopPreview();
    setSelectedAudio(null);
  };

  const handlePlatformToggle = (platformId: string) => {
    setSelectedPlatforms((prev) =>
      prev.includes(platformId)
        ? prev.filter((id) => id !== platformId)
        : [...prev, platformId],
    );
  };

  const handlePublish = async () => {
    if (selectedPlatforms.length === 0) return;
    setPublishing(true);
    try {
      const audioConfig =
        hasReelsPlatform && selectedAudio
          ? {
              ...selectedAudio,
              audio_volume: audioVolume,
              video_volume: videoVolume,
            }
          : undefined;
      await onPublish(selectedPlatforms, audioConfig);
      onClose();
    } catch (error) {
      console.error("Erro ao publicar:", error);
    } finally {
      setPublishing(false);
    }
  };

  const hasVideo = post?.video_info?.filename;
  const hasImage = post?.image_info?.filename;
  const isLoading = publishing || loading;

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={t("Publicar em Múltiplas Plataformas")}
      maxWidth="max-w-2xl"
      footer={
        <div className="flex flex-row justify-end gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            {t("Cancelar")}
          </Button>
          <Button
            type="button"
            variant="primary"
            icon={
              isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Share2 className="w-4 h-4" />
              )
            }
            onClick={handlePublish}
            disabled={selectedPlatforms.length === 0 || isLoading}
          >
            {isLoading
              ? t("Publicando...")
              : selectedPlatforms.length === 1
                ? t("Publicar em {count} plataforma", {
                    count: selectedPlatforms.length,
                  })
                : t("Publicar em {count} plataformas", {
                    count: selectedPlatforms.length,
                  })}
          </Button>
        </div>
      }
    >
      <div className="py-2 space-y-6">
        {/* Header */}
        <div className="flex items-start gap-4">
          <div className="shrink-0 w-12 h-12 rounded-full flex items-center justify-center bg-purple-100 text-purple-600">
            <Share2 className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-gray-900 mb-1">
              {t("Selecione as plataformas")}
            </h3>
            <p className="text-sm text-gray-600">
              {t("O post")}{" "}
              <span className="font-semibold text-gray-900">#{post?.id}</span>{" "}
              {t("será publicado nas plataformas escolhidas.")}
            </p>
          </div>
        </div>

        {/* Post info */}
        <div className="p-4 bg-purple-50 rounded-lg border border-purple-300">
          <h3 className="font-medium text-gray-900 mb-2">
            {t("Post #{id}", { id: post?.id })}
          </h3>
          <p className="text-sm text-gray-700 mb-2 whitespace-pre-wrap wrap-break-word line-clamp-3">
            {post?.content}
          </p>
          <div className="flex gap-2 text-xs flex-wrap">
            {hasImage && (
              <span className="px-2 py-1 bg-green-100 text-green-800 rounded-sm inline-flex items-center gap-1">
                <ImageIcon className="h-3 w-3" />
                {t("Imagem")}
              </span>
            )}
            {hasVideo && (
              <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-sm inline-flex items-center gap-1">
                <VideoIcon className="h-3 w-3" />
                {t("Vídeo")}
              </span>
            )}
          </div>
        </div>

        {/* Conflict warning */}
        {hasImage && hasVideo && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-600 shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium">{t("Atenção:")}</p>
                <p>
                  {t(
                    "Este post tem imagem e vídeo. O LinkedIn publicará apenas o vídeo (ignora imagem).",
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Platform list */}
        <div className="space-y-3">
          <h3 className="font-medium text-gray-900">
            {t("Plataformas disponíveis")}
          </h3>
          {PLATFORMS.map((platform) => {
            const isSelected = selectedPlatforms.includes(platform.id);
            const canSelect = !platform.requiresVideo || hasVideo;
            const isDisabled = platform.disabled || !canSelect || publishing;

            return (
              <div
                key={platform.id}
                className={cn(
                  "p-4 border rounded-lg transition-all",
                  isDisabled && "opacity-50 cursor-not-allowed bg-gray-100",
                  !isDisabled && "cursor-pointer hover:border-purple-300",
                  isSelected && !isDisabled && "border-purple-500 bg-purple-50",
                  !isSelected && "border-purple-300",
                )}
                onClick={() => !isDisabled && handlePlatformToggle(platform.id)}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="flex items-center text-purple-600 shrink-0">
                      {platform.icon}
                    </span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-gray-900">
                          {platform.name}
                        </h4>
                        {"supportsAudio" in platform &&
                          platform.supportsAudio && (
                            <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-sm bg-green-100 text-green-700">
                              <Music className="h-3 w-3" />
                              {t("Suporta trilha")}
                            </span>
                          )}
                      </div>
                      <p
                        className={cn(
                          "text-sm",
                          platform.disabled ? "text-red-600" : "text-gray-600",
                        )}
                      >
                        {t(platform.description)}
                      </p>
                      {platform.requiresVideo && !hasVideo && (
                        <p className="text-xs text-red-600 mt-1">
                          ⚠️ {t("Requer vídeo")}
                        </p>
                      )}
                      {platform.warning && (
                        <p className="text-xs text-orange-600 mt-1 font-medium">
                          {t(platform.warning)}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center shrink-0">
                    {isSelected && !isDisabled && (
                      <Check className="w-5 h-5 text-purple-600" />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Audio selector — only shown when Reels platform is selected */}
        {hasReelsPlatform && (
          <div className="border border-green-200 rounded-lg overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border-b border-green-200">
              <Music className="h-4 w-4 text-green-600 shrink-0" />
              <span className="text-sm font-medium text-green-800">
                {t("Trilha sonora para Reels (Meta Sound Collection)")}
              </span>
              <span className="ml-auto text-xs text-green-600">
                {t("Opcional")}
              </span>
            </div>

            <div className="p-4 space-y-4">
              {/* Selected track */}
              {selectedAudio && (
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                  <Music className="h-4 w-4 text-green-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {selectedAudio.track.title}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {selectedAudio.track.artist}
                    </p>
                  </div>
                  <button
                    onClick={clearTrack}
                    className="text-gray-400 hover:text-gray-600 shrink-0"
                    title={t("Remover trilha")}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}

              {/* Volume sliders — only when a track is selected */}
              {selectedAudio && (
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between mb-1">
                      <label className="text-xs text-gray-600">
                        {t("Volume da música")}
                      </label>
                      <span className="text-xs font-medium text-gray-800">
                        {audioVolume}%
                      </span>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      value={audioVolume}
                      onChange={(e) => setAudioVolume(Number(e.target.value))}
                      className="w-full accent-green-600"
                    />
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <label className="text-xs text-gray-600">
                        {t("Volume do vídeo original")}
                      </label>
                      <span className="text-xs font-medium text-gray-800">
                        {videoVolume}%
                      </span>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      value={videoVolume}
                      onChange={(e) => setVideoVolume(Number(e.target.value))}
                      className="w-full accent-purple-600"
                    />
                  </div>
                </div>
              )}

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  value={audioSearch}
                  onChange={(e) => setAudioSearch(e.target.value)}
                  placeholder={t("Buscar música ou artista...")}
                  className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-green-400"
                />
                {loadingAudio && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
                )}
              </div>

              {/* Error */}
              {audioError && (
                <div className="flex items-start gap-2 p-3 bg-red-50 rounded-lg text-sm text-red-700">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                  <p>{audioError}</p>
                </div>
              )}

              {/* Track list */}
              {!audioError && (
                <div className="space-y-1 max-h-52 overflow-y-auto">
                  {audioTracks.length === 0 && !loadingAudio && (
                    <p className="text-sm text-gray-500 text-center py-4">
                      {audioSearch
                        ? t("Nenhuma música encontrada")
                        : t("Digite para buscar músicas")}
                    </p>
                  )}
                  {audioTracks.map((track) => {
                    const isPlaying = playingId === track.id;
                    const isChosen = selectedAudio?.audio_id === track.id;
                    return (
                      <div
                        key={track.id}
                        className={cn(
                          "flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors",
                          isChosen
                            ? "bg-green-100 border border-green-300"
                            : "hover:bg-gray-50 border border-transparent",
                        )}
                        onClick={() => selectTrack(track)}
                      >
                        {/* Play/Stop button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            togglePreview(track);
                          }}
                          disabled={!track.preview_url}
                          className={cn(
                            "shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-colors",
                            track.preview_url
                              ? "hover:bg-green-100 text-green-600"
                              : "text-gray-300 cursor-not-allowed",
                            isPlaying && "bg-green-100",
                          )}
                          title={
                            track.preview_url
                              ? isPlaying
                                ? t("Parar preview")
                                : t("Ouvir preview")
                              : t("Preview indisponível")
                          }
                        >
                          {isPlaying ? (
                            <Square className="h-3 w-3 fill-current" />
                          ) : (
                            <Play className="h-3 w-3 fill-current" />
                          )}
                        </button>

                        {/* Cover art */}
                        {track.cover_art_url ? (
                          <img
                            src={track.cover_art_url}
                            alt={track.title}
                            className="shrink-0 w-8 h-8 rounded-sm object-cover"
                          />
                        ) : (
                          <div className="shrink-0 w-8 h-8 rounded-sm bg-gray-200 flex items-center justify-center">
                            <Music className="h-4 w-4 text-gray-400" />
                          </div>
                        )}

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {track.title}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {track.artist}
                          </p>
                        </div>

                        {/* Duration + check */}
                        <div className="flex items-center gap-2 shrink-0">
                          {track.duration_sec != null && (
                            <span className="text-xs text-gray-400">
                              {formatDuration(track.duration_sec)}
                            </span>
                          )}
                          {isChosen && (
                            <Check className="h-4 w-4 text-green-600" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <p className="text-xs text-gray-400">
                {t(
                  "Músicas da Meta Sound Collection. Disponibilidade pode variar por região.",
                )}
              </p>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default MultiPlatformPublishModal;
